import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/designSystem/Input';
import { Button } from '@/designSystem/Button';
import { SearchIcon, Loader2 } from 'lucide-react';

interface SearchDropdownProps<T> {
  // Search function receives search term and returns search results
  onSearch: (searchTerm: string) => Promise<T[]>;
  // Render function for each result item
  renderItem: (item: T) => React.ReactNode;
  // Function to call when an item is selected
  onSelectItem: (item: T) => void;
  // Function to get unique key for each item
  getItemKey: (item: T) => string | number;
  // Search input placeholder
  placeholder?: string;
  // Minimum search term length before triggering search
  minSearchLength?: number;
  // Custom empty state message
  emptyMessage?: string;
  // Custom loading message
  loadingMessage?: string;
  // External loading state (e.g. from react-query)
  isLoading?: boolean;
  // Optional callback for input change
  onInputChange?: (value: string) => void;
}

export function SearchDropdown<T>({
  onSearch,
  renderItem,
  onSelectItem,
  getItemKey,
  placeholder = 'Search...',
  minSearchLength = 3,
  emptyMessage = 'No results found',
  loadingMessage = 'Searching...',
  isLoading = false,
  onInputChange,
}: SearchDropdownProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [results, setResults] = useState<T[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch search results whenever onSearch prop changes
  // This doesn't include debouncing - we expect parent to handle that
  useEffect(() => {
    const fetchResults = async () => {
      if (searchTerm.length < minSearchLength) {
        setIsDropdownOpen(false);
        setResults([]);
        return;
      }

      try {
        const searchResults = await onSearch(searchTerm);
        setResults(searchResults);
        setIsDropdownOpen(searchResults.length > 0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      }
    };

    fetchResults();
  }, [onSearch, searchTerm, minSearchLength]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Call the optional onInputChange handler if provided
    if (onInputChange) {
      onInputChange(value);
    }
  };

  const handleSelectResult = (item: T) => {
    onSelectItem(item);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const handleSearchButtonClick = async () => {
    if (searchTerm.length < minSearchLength || isLoading) return;

    try {
      const searchResults = await onSearch(searchTerm);
      setResults(searchResults);
      setIsDropdownOpen(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    }
  };

  return (
    <div className="relative">
      <div className="flex">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            placeholder={placeholder}
            onFocus={() => searchTerm.length >= minSearchLength && results.length > 0 && setIsDropdownOpen(true)}
            className="pr-10"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearchButtonClick();
              }
            }}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
        <Button
          type="button"
          className="ml-2"
          disabled={searchTerm.length < minSearchLength || isLoading}
          onClick={handleSearchButtonClick}
        >
          <SearchIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Dropdown Results */}
      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">{loadingMessage}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            <ul>
              {results.map((item) => (
                <li
                  key={getItemKey(item)}
                  className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectResult(item)}
                >
                  {renderItem(item)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}